import React, { useState, useRef } from 'react';
import { TallyEntry } from '../../types/tally';
import { readExcelFile } from '../../lib/tally/tally.excel';
import { parseExcelData } from '../../lib/tally/tally.parser';
import { convertToXML } from '../../lib/tally/tally.xml';

interface ExcelUploaderProps {
  onDataParsed: (data: TallyEntry[]) => void;
  onXmlGenerated?: (xml: string) => void;
}

const ExcelUploader: React.FC<ExcelUploaderProps> = ({
  onDataParsed,
  onXmlGenerated,
}) => {
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [parsedData, setParsedData] = useState<TallyEntry[]>([]);
  const [generatedXml, setGeneratedXml] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    setFileName(file.name);
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Read Excel file
      const excelData = await readExcelFile(file);

      if (excelData && excelData.length > 0) {
        console.log(
          'Excel data read successfully:',
          excelData.length,
          'records',
        );

        // Parse Excel data into TallyEntry format
        const tallyEntries = parseExcelData(excelData);

        if (tallyEntries && tallyEntries.length > 0) {
          console.log(
            'Excel data parsed into Tally format:',
            tallyEntries.length,
            'entries',
          );
          setParsedData(tallyEntries);
          onDataParsed(tallyEntries);

          // Generate XML
          const xml = convertToXML(tallyEntries);
          setGeneratedXml(xml);
          if (onXmlGenerated) {
            onXmlGenerated(xml);
          }

          setSuccess(`Successfully processed ${tallyEntries.length} entries`);
        } else {
          throw new Error('Failed to parse Excel data into Tally format');
        }
      } else {
        throw new Error('No valid data found in Excel file');
      }
    } catch (err) {
      setError(
        `Failed to process Excel file: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
      console.error('Excel processing error:', err);
      onDataParsed([]);
    } finally {
      setLoading(false);
    }
  };

  const sendToTally = async () => {
    if (!generatedXml) {
      setError('No XML data to send. Please upload a file first.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Use the proxy endpoint defined in the Vite config
      const response = await fetch('/tally-api', {
        method: 'POST',
        headers: {
          'Content-Type': 'text/xml',
        },
        body: generatedXml,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseText = await response.text();

      if (responseText.includes('<STATUS>0</STATUS>')) {
        setSuccess('Successfully sent data to Tally');
      } else if (responseText.includes('DESC not found')) {
        throw new Error(
          'XML formatting error: DESC not found. Check company name.',
        );
      } else if (responseText.includes('<LINEERROR>')) {
        const errorMatch = responseText.match(/<LINEERROR>(.*?)<\/LINEERROR>/);
        throw new Error(
          errorMatch ? errorMatch[1] : 'Unknown error in Tally response',
        );
      } else {
        throw new Error(
          `Unexpected response from Tally: ${responseText.substring(0, 100)}...`,
        );
      }
    } catch (err) {
      setError(
        `Failed to send to Tally: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
      console.error('Tally API error:', err);
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Function to download the XML
  const downloadXml = () => {
    if (!generatedXml) {
      setError('No XML data to download. Please upload a file first.');
      return;
    }

    const blob = new Blob([generatedXml], { type: 'text/xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `tally_import_${new Date().toISOString().substring(0, 10)}.xml`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="flex flex-col gap-4 w-full max-w-md">
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors
          ${loading ? 'bg-gray-100 pointer-events-none' : ''}`}
        onClick={triggerFileInput}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls,.csv"
          className="hidden"
        />

        {loading ? (
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-2">Processing file...</p>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48">
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12m32-12h-4m-8-4h.01M20 16h.01"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <div className="space-y-1">
              {fileName ? (
                <>
                  <p className="text-sm font-medium">
                    Selected file: {fileName}
                  </p>
                  <p className="text-xs text-gray-500">
                    Click to select a different file
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">
                    Excel file (.xlsx, .xls, .csv)
                  </p>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {parsedData.length > 0 && (
        <div className="mt-2 space-y-3">
          <p className="text-sm font-medium">
            Parsed {parsedData.length} entries from Excel
          </p>

          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <button
              onClick={sendToTally}
              disabled={loading}
              className="flex-1 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Sending to Tally...' : 'Send to Tally'}
            </button>

            <button
              onClick={downloadXml}
              disabled={loading}
              className="flex-1 py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed">
              Download XML
            </button>
          </div>

          <div className="mt-4">
            <h3 className="text-sm font-medium mb-2">
              Preview of Parsed Data:
            </h3>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto">
              <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                {JSON.stringify(parsedData.slice(0, 2), null, 2)}
              </pre>
              {parsedData.length > 2 && (
                <p className="text-xs text-gray-500 mt-2">{`... and ${parsedData.length - 2} more entries`}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}
    </div>
  );
};

export default ExcelUploader;
